/**
 * Menu: a button that opens a list of actions or links (role="menu") (cockatrice-ui `src/ui/Menu.jsx`, ported to
 * hyperscript; PLAN.md §0).
 * Keyboard: Enter, Space or ↓ opens on the first item, ↑ on the last; ↑ ↓ Home End move; a letter jumps;
 * Escape closes and returns focus to the button; Tab, the focus leaving or clicking outside closes. Choosing an item
 * closes the menu and puts the focus back on the button first, then runs the item, so an item that moves the focus (a
 * dialog it opens, a field it goes to) keeps it there.
 * On phones the list docks to the bottom of the screen like an action sheet, so it can never be cut off.
 *
 * items: [{ label, hint?, icon?, kbd?, href?, onSelect?, danger?, disabled?, checked?, keepOpen?, dialog?, class? } | { separator: true } | { heading: "…" }]
 * An item's label, hint and disabled may be getters: the item follows them.
 */
import './css/menu.css';
import { createEffect, createSignal, createUniqueId, onCleanup } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, each, el, spread, when } from './solid.ts';
import type { Child } from './solid.ts';

/** An item of a menu: an action, a link or a checkable choice. */
export interface MenuItem {
  /** Item text. */
  readonly label: string;
  /** Second line in small muted text. */
  readonly hint?: string | undefined;
  /** Leading icon name. */
  readonly icon?: IconName | undefined;
  /** Keyboard shortcut shown on the right. */
  readonly kbd?: string | undefined;
  /** Makes the item a link. */
  readonly href?: string | undefined;
  /** Called when chosen. */
  readonly onSelect?: ((e: MouseEvent) => void) | undefined;
  /** Red, for destructive actions. Put these last. */
  readonly danger?: boolean | undefined;
  /** Shown but cannot be chosen. */
  readonly disabled?: boolean | undefined;
  /** Makes it a checkable item with a tick. */
  readonly checked?: boolean | undefined;
  /** With checked: a single-choice item (menuitemradio). */
  readonly radio?: boolean | undefined;
  /** Keep the menu open after choosing. */
  readonly keepOpen?: boolean | undefined;
  /** Opens a dialog (aria-haspopup="dialog"); end its label with "…". */
  readonly dialog?: boolean | undefined;
  /** Extra classes on the item (a hook for tests and the page). */
  readonly class?: string | undefined;
}

export type MenuEntry =
  | MenuItem
  | {
      /** A dividing line. */
      readonly separator: true;
    }
  | {
      /** A small uppercase group heading. */
      readonly heading: string;
    };

/**
 * What a custom trigger spreads onto its <button> (`el('button', spread(props, { … }))`). `aria-expanded` and
 * `aria-label` are getters, so a spread keeps them up to date.
 */
export interface MenuTriggerProps {
  readonly ref: (el: HTMLElement) => void;
  readonly id: string;
  readonly type: 'button';
  readonly 'aria-haspopup': 'menu';
  readonly 'aria-expanded': 'true' | 'false';
  readonly 'aria-controls': string;
  readonly 'aria-label'?: string | undefined;
  readonly onClick: () => void;
  readonly onKeyDown: (e: KeyboardEvent) => void;
}

export interface MenuProps {
  /** Trigger text, or its aria-label when iconOnly. */
  readonly label: string;
  /** Icon-only trigger (default icon "more"). */
  readonly iconOnly?: boolean | undefined;
  /** Icon on the trigger. */
  readonly icon?: IconName | undefined;
  /** Chevron after text triggers. Default true. */
  readonly chevron?: boolean | undefined;
  /** The entries. */
  readonly items: readonly MenuEntry[];
  /** Trigger edge the list lines up with. Default "end". Flips if it would leave the screen. */
  readonly placement?: 'start' | 'end' | undefined;
  /** List width (CSS length). Default 248px. */
  readonly width?: string | undefined;
  /** Render your own trigger: spread these props onto a <button>. */
  readonly trigger?: ((props: MenuTriggerProps) => Child) | undefined;
  /** Replace the built-in trigger's classes. */
  readonly triggerClass?: string | undefined;
  /** Called when the menu opens or closes. */
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  /** Always open, in place (previews). */
  readonly inline?: boolean | undefined;
  /** Extra class on the wrapper. */
  readonly class?: string | undefined;
}

/** A button that opens a list of actions or links. Docks to the bottom on phones. */
export default function Menu(props: MenuProps): Child {
  const id = createUniqueId();
  const triggerId = `ui-menu-${id}-btn`;
  const listId = `ui-menu-${id}-list`;
  const [open, setOpen] = createSignal(false);
  const [flipX, setFlipX] = createSignal(false);
  const [up, setUp] = createSignal(false);
  let root: HTMLDivElement | undefined;
  let triggerEl: HTMLElement | undefined;
  let listEl: HTMLDivElement | undefined;

  const isOpen = (): boolean => props.inline === true || open();
  const placement = (): 'start' | 'end' => (props.placement === 'start' ? 'start' : 'end');
  const els = (): HTMLElement[] => (listEl ? [...listEl.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])')] : []);
  const focusAt = (i: number): void => {
    const list = els();
    if (list.length > 0) list[((i % list.length) + list.length) % list.length]?.focus();
  };

  const place = (): void => {
    setFlipX(false);
    setUp(false);
    if (!listEl || window.matchMedia('(max-width: 720px)').matches) return;
    const r = listEl.getBoundingClientRect();
    if (r.right > window.innerWidth - 8 || r.left < 8) setFlipX(true);
    if (r.bottom > window.innerHeight - 8 && r.top - r.height > 8) setUp(true);
  };

  const show = (where: 'first' | 'last' = 'first'): void => {
    setOpen(true);
    props.onOpenChange?.(true);
    requestAnimationFrame(() => {
      // Closed again before the frame (an item chosen at once): nothing to place, and the focus stays where it went.
      if (!open()) return;
      place();
      focusAt(where === 'last' ? -1 : 0);
    });
  };
  const hide = (refocus = true): void => {
    if (!open()) return;
    setOpen(false);
    props.onOpenChange?.(false);
    if (refocus) triggerEl?.focus();
  };

  createEffect(() => {
    if (!open() || props.inline === true) return;
    const away = (e: PointerEvent): void => {
      if (!(e.target instanceof Node && root?.contains(e.target) === true)) hide(false);
    };
    document.addEventListener('pointerdown', away);
    onCleanup(() => { document.removeEventListener('pointerdown', away); });
  });

  const onTriggerKey = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      show('first');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      show('last');
    }
  };

  const onListKey = (e: KeyboardEvent): void => {
    const list = els();
    const i = list.findIndex((node) => node === document.activeElement);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusAt(i + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusAt(i - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusAt(-1);
        break;
      case 'Escape':
        if (props.inline === true) return;
        e.preventDefault();
        e.stopPropagation();
        hide(true);
        break;
      case 'Tab':
        if (props.inline !== true) hide(false);
        break;
      default:
        if (e.key.length === 1 && /\S/u.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
          const k = e.key.toLowerCase();
          const order = [...list.slice(i + 1), ...list.slice(0, i + 1)];
          const hit = order.find((node) => node.textContent.trim().toLowerCase().startsWith(k));
          if (hit) {
            e.preventDefault();
            hit.focus();
          }
        }
    }
  };

  const select = (item: MenuItem, e: MouseEvent): void => {
    if (item.disabled === true) {
      e.preventDefault();
      return;
    }
    // Closed first, the focus back on the button, so whatever the item does with the focus stands.
    if (item.keepOpen !== true && props.inline !== true) hide(item.href === undefined || item.href === '');
    item.onSelect?.(e);
  };

  const triggerProps: MenuTriggerProps = {
    ref: (node: HTMLElement) => {
      triggerEl = node;
    },
    id: triggerId,
    type: 'button',
    'aria-haspopup': 'menu',
    get 'aria-expanded'() {
      return isOpen() ? 'true' : 'false';
    },
    'aria-controls': listId,
    get 'aria-label'() {
      return props.iconOnly === true ? props.label : undefined;
    },
    onClick: () => {
      if (open()) hide(false);
      else show('first');
    },
    onKeyDown: onTriggerKey,
  };

  const item = (entry: MenuItem): Child => {
    const link = entry.href !== undefined && entry.href !== '' && entry.disabled !== true;
    return el(
      link ? 'a' : 'button',
      {
        href: link ? entry.href : undefined,
        type: entry.href !== undefined && entry.href !== '' ? undefined : 'button',
        class: () => `${entry.danger === true ? 'ui-menu__item ui-menu__item--danger' : 'ui-menu__item'}${entry.class !== undefined && entry.class !== '' ? ` ${entry.class}` : ''}`,
        role: entry.checked === undefined ? 'menuitem' : entry.radio === true ? 'menuitemradio' : 'menuitemcheckbox',
        'aria-checked': entry.checked === undefined ? undefined : entry.checked ? 'true' : 'false',
        'aria-disabled': () => (entry.disabled === true ? 'true' : undefined),
        'aria-haspopup': entry.dialog === true ? 'dialog' : undefined,
        tabindex: '-1',
        onClick: (e: MouseEvent) => {
          select(entry, e);
        },
      },
      entry.icon === undefined ? null : component(Icon, { name: entry.icon, class: 'ui-menu__icon' }),
      el(
        'span',
        { class: 'ui-menu__label' },
        () => entry.label,
        when(
          () => entry.hint !== undefined && entry.hint !== '',
          () => el('small', {}, () => entry.hint),
        ),
      ),
      entry.kbd === undefined || entry.kbd === '' ? null : el('kbd', { class: 'ui-menu__kbd' }, entry.kbd),
      entry.checked === true ? component(Icon, { name: 'check', size: 'sm', class: 'ui-menu__check' }) : null,
    );
  };

  return el(
    'div',
    {
      ref: (node: HTMLDivElement) => {
        root = node;
      },
      // The focus leaving for anywhere outside closes it (a press does too, below).
      onFocusOut: (e: FocusEvent) => {
        const next = e.relatedTarget;
        if (props.inline !== true && next instanceof Node && root?.contains(next) !== true) hide(false);
      },
      class: () =>
        [
          `ui-menu ui-menu--${placement()}${props.inline === true ? ' ui-menu--inline' : ''}${props.class !== undefined && props.class !== '' ? ` ${props.class}` : ''}`,
          flipX() && 'ui-menu--flip',
          up() && 'ui-menu--up',
        ]
          .filter(Boolean)
          .join(' '),
    },
    when(
      () => props.trigger !== undefined,
      () => props.trigger?.(triggerProps),
      () =>
        el(
          'button',
          spread(triggerProps, {
            class: () => props.triggerClass ?? (props.iconOnly === true ? 'ui-menu__trigger ui-menu__trigger--icon' : 'ui-menu__trigger'),
          }),
          when(
            () => props.icon !== undefined || props.iconOnly === true,
            () =>
              component(Icon, {
                get name() {
                  return props.icon ?? 'more';
                },
              }),
          ),
          when(
            () => props.iconOnly !== true,
            () => [
              el('span', {}, () => props.label),
              when(
                () => props.chevron !== false,
                () => component(Icon, { name: 'chevronDown', size: 'sm', class: 'ui-menu__chev' }),
              ),
            ],
          ),
        ),
    ),
    when(
      () => open() && props.inline !== true,
      () => el('div', { class: 'ui-menu__scrim', 'aria-hidden': 'true', onClick: () => { hide(true); } }),
    ),
    el(
      'div',
      {
        ref: (node: HTMLDivElement) => {
          listEl = node;
        },
        id: listId,
        class: 'ui-menu__list',
        role: 'menu',
        'aria-labelledby': triggerId,
        hidden: () => !isOpen(),
        onKeyDown: onListKey,
        style: () => (props.width !== undefined && props.width !== '' ? { '--ui-menu-w': props.width } : undefined),
      },
      each(
        () => props.items,
        (entry) =>
          'separator' in entry
            ? el('div', { class: 'ui-menu__sep', role: 'separator' })
            : 'heading' in entry
              ? el('div', { class: 'ui-menu__heading', role: 'presentation' }, entry.heading)
              : item(entry),
      ),
    ),
  );
}
